import { sanityClient } from './client';
import { QUERIES } from './queries';
import type {
  PortfolioData,
  SanityPerson,
  SanityProject,
  SanityCertification,
  SanityEducation,
  SanityWorkExperience,
  SanityFreelance,
} from './types';

export class SanityServiceError extends Error {
  constructor(
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'SanityServiceError';
  }
}

export const SanityService = {
  /**
   * Fetch all portfolio data in a single request
   */
  fetchPortfolioData: async (): Promise<PortfolioData> => {
    try {
      const data = await sanityClient.fetch<PortfolioData>(QUERIES.portfolioData);
      return {
        person: data.person,
        projects: data.projects || [],
        certifications: data.certifications || [],
        education: data.education || [],
        workExperience: data.workExperience || [],
        freelance: data.freelance || null,
      };
    } catch (error) {
      console.error('Failed to fetch portfolio data from Sanity:', error);
      throw new SanityServiceError('Failed to fetch portfolio data', error);
    }
  },

  /**
   * Fetch person data only
   */
  fetchPerson: async (): Promise<SanityPerson | null> => {
    try {
      return await sanityClient.fetch<SanityPerson>(QUERIES.person);
    } catch (error) {
      throw new SanityServiceError('Failed to fetch person data', error);
    }
  },

  /**
   * Fetch projects only
   */
  fetchProjects: async (): Promise<SanityProject[]> => {
    try {
      return await sanityClient.fetch<SanityProject[]>(QUERIES.projects);
    } catch (error) {
      throw new SanityServiceError('Failed to fetch projects', error);
    }
  },

  /**
   * Fetch certifications only
   */
  fetchCertifications: async (): Promise<SanityCertification[]> => {
    try {
      return await sanityClient.fetch<SanityCertification[]>(QUERIES.certifications);
    } catch (error) {
      throw new SanityServiceError('Failed to fetch certifications', error);
    }
  },

  /**
   * Fetch education only
   */
  fetchEducation: async (): Promise<SanityEducation[]> => {
    try {
      return await sanityClient.fetch<SanityEducation[]>(QUERIES.education);
    } catch (error) {
      throw new SanityServiceError('Failed to fetch education', error);
    }
  },

  /**
   * Fetch work experience only
   */
  fetchWorkExperience: async (): Promise<SanityWorkExperience[]> => {
    try {
      return await sanityClient.fetch<SanityWorkExperience[]>(QUERIES.workExperience);
    } catch (error) {
      throw new SanityServiceError('Failed to fetch work experience', error);
    }
  },

  /**
   * Fetch freelance data only
   */
  fetchFreelance: async (): Promise<SanityFreelance | null> => {
    try {
      return await sanityClient.fetch<SanityFreelance>(QUERIES.freelance);
    } catch (error) {
      throw new SanityServiceError('Failed to fetch freelance data', error);
    }
  },
};

// Re-export types
export * from './types';
export { sanityClient } from './client';
export { QUERIES } from './queries';
